/**
 * Shared behaviour for the Coast Mountain Sports sections (built by tools/build-sections.js):
 * copy-to-clipboard buttons, the Pro Shop tune finder and rate tabs, the gift card amounts and tilt,
 * and store hours / open-now / today's daylight for Visit (also used by the trip map's packing list).
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const data = (root) => { const el = root.querySelector('script[data-cms]'); try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; } };

/* ---- Whitehorse time, sun and hours ---- */
const LAT = 60.7212, LON = -135.0568, TZ = -7; // Yukon stays on UTC-7 all year
const ytNow = () => new Date(Date.now() + TZ * 3600e3);
const dayOfYear = (d) => Math.floor((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 864e5) + 1;
function solar(N) {
  const g = 2 * Math.PI / 365 * (N - 1);
  const eq = 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const d = 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g);
  const phi = LAT * Math.PI / 180;
  const c = Math.max(-1, Math.min(1, Math.cos(90.833 * Math.PI / 180) / (Math.cos(phi) * Math.cos(d)) - Math.tan(phi) * Math.tan(d)));
  const ha = Math.acos(c) * 180 / Math.PI;
  return { rise: 720 - 4 * (LON + ha) - eq + TZ * 60, set: 720 - 4 * (LON - ha) - eq + TZ * 60, day: 8 * ha };
}
function clock(m) { m = ((Math.round(m) % 1440) + 1440) % 1440; const h = Math.floor(m / 60), mm = m % 60, ap = h >= 12 ? 'pm' : 'am', h12 = h % 12 || 12; return h12 + ':' + (mm < 10 ? '0' : '') + mm + ' ' + ap; }
function short(m) { const h = Math.floor(m / 60) % 24, ap = h >= 12 ? 'pm' : 'am', h12 = h % 12 || 12; return m % 60 ? clock(m) : h12 + ' ' + ap; }
const mins = (t, fallback) => { const m = /^(\d{1,2}):?(\d{2})?$/.exec(String(t || '').trim()); return m ? +m[1] * 60 + +(m[2] || 0) : fallback; };

function makeHours(cfg) {
  const wk = [mins(cfg.weekdayOpen, 600), mins(cfg.weekdayClose, 1080)];
  const su = [mins(cfg.sundayOpen, 660), mins(cfg.sundayClose, 1020)];
  const eve = mins(cfg.eveClose, 900); // December 24 and 31
  // statutory holidays use the Sunday hours (one YYYY-MM-DD per line in the Visit settings)
  const holidays = new Set(String(cfg.holidays || '').split(/[s,]+/).filter((x) => /^d{4}-d{2}-d{2}$/.test(x)));
  const isHoliday = (d) => holidays.has(d.toISOString().slice(0, 10)); // d is already shifted to Yukon time
  function hoursFor(d) {
    const mo = d.getUTCMonth(), da = d.getUTCDate(), dow = d.getUTCDay();
    if ((mo === 11 && da === 25) || (mo === 0 && da === 1)) return null;
    if (mo === 11 && (da === 24 || da === 31)) return [wk[0], eve];
    return dow === 0 || isHoliday(d) ? su : wk;
  }
  function status() {
    const d = ytNow(), m = d.getUTCHours() * 60 + d.getUTCMinutes(), h = hoursFor(d);
    if (h && m >= h[0] && m < h[1]) return { open: true, text: 'Open now until ' + short(h[1]), shortText: 'Open until ' + short(h[1]) };
    if (h && m < h[0]) return { open: false, text: 'Closed. Opens today at ' + short(h[0]), shortText: 'Opens at ' + short(h[0]) };
    for (let i = 1; i < 7; i++) {
      const nd = new Date(d.getTime() + i * 864e5), nh = hoursFor(nd);
      if (nh) { const dn = i === 1 ? 'tomorrow' : nd.toLocaleDateString('en-CA', { weekday: 'long', timeZone: 'UTC' }); return { open: false, text: 'Closed. Opens ' + dn + ' at ' + short(nh[0]), shortText: 'Opens ' + dn + ' ' + short(nh[0]) }; }
    }
    return { open: false, text: 'Closed', shortText: 'Closed' };
  }
  function rows() {
    const d = ytNow(), dow = d.getUTCDay(), mo = d.getUTCMonth(), da = d.getUTCDate();
    const special = (mo === 11 && (da === 24 || da === 25 || da === 31)) || (mo === 0 && da === 1);
    const hol = !special && isHoliday(d);
    const span = (a) => short(a[0]) + ' to ' + short(a[1]);
    return [
      ['Monday to Saturday', span(wk), !special && !hol && dow >= 1 && dow <= 6],
      ['Sundays and statutory holidays', span(su), !special && (hol || dow === 0)],
      ['December 24 and 31', span([wk[0], eve]), mo === 11 && (da === 24 || da === 31)],
      ['December 25 and January 1', 'Closed', (mo === 11 && da === 25) || (mo === 0 && da === 1)],
    ];
  }
  return { status, rows };
}

/* ---- copy buttons ---- */
function wireCopy(root) {
  root.querySelectorAll('.copy[data-copy]').forEach((b) => {
    if (b.dataset.wired) return; b.dataset.wired = '1';
    b.addEventListener('click', () => {
      const v = b.dataset.copy, orig = b.textContent, label = b.getAttribute('aria-label');
      const done = () => {
        b.textContent = 'Copied'; if (label) b.setAttribute('aria-label', 'Copied');
        setTimeout(() => { b.textContent = orig; if (label) b.setAttribute('aria-label', label); }, 1400);
      };
      const select = () => { const r = document.createRange(); r.selectNodeContents(b); const s = getSelection(); s.removeAllRanges(); s.addRange(r); };
      try { navigator.clipboard.writeText(v).then(done, select); } catch (e) { select(); }
    });
  });
}

/* ---- Pro Shop ---- */
function initPro(root) {
  const cfg = data(root);
  const tuneBox = root.querySelector('.tune'), choices = [...root.querySelectorAll('.choice')];
  function showTune(i) {
    const t = (cfg.tunes || [])[i]; if (!t || !tuneBox) return;
    tuneBox.innerHTML = '<div class="tt">' + esc(t.title) + '</div><div class="td">' + esc(t.desc) + '</div><div class="tp"><b>' + esc(t.price) + '</b><span>' + esc(t.extra) + '</span></div>';
    choices.forEach((c) => c.setAttribute('aria-pressed', String(+c.dataset.t === i)));
  }
  choices.forEach((c) => c.addEventListener('click', () => showTune(+c.dataset.t)));
  const start = choices.findIndex((c) => c.dataset.default === 'true');
  showTune(start >= 0 ? start : 0);

  const tabs = root.querySelector('.tabs'), list = root.querySelector('.rate-list'), groups = {};
  (cfg.rates || []).forEach((r) => { (groups[r.group] = groups[r.group] || []).push(r); });
  const names = Object.keys(groups);
  const setRates = (k) => { list.innerHTML = groups[k].map((r) => '<li><span>' + esc(r.service) + '</span><span>' + esc(r.price) + '</span></li>').join(''); };
  names.forEach((k, i) => {
    const b = document.createElement('button'); b.className = 'tab'; b.type = 'button'; b.setAttribute('role', 'tab');
    b.id = root.id + '-tab-' + i; b.textContent = k; b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
    b.addEventListener('click', () => { setRates(k); tabs.querySelectorAll('.tab').forEach((x) => x.setAttribute('aria-selected', String(x === b))); list.setAttribute('aria-labelledby', b.id); });
    tabs.appendChild(b);
  });
  if (names.length) { setRates(names[0]); list.setAttribute('aria-labelledby', root.id + '-tab-0'); }
}

/* ---- Gift cards ---- */
function initGift(root) {
  const cfg = data(root);
  const wrap = root.querySelector('.amounts'), amt = root.querySelector('.gc-amt'), buy = root.querySelector('.gc-buy');
  const amounts = cfg.amounts || [];
  const pick = (a) => {
    wrap.querySelectorAll('.amt').forEach((x) => x.setAttribute('aria-checked', String(+x.dataset.v === a.value)));
    amt.textContent = a.label;
    buy.textContent = (cfg.buyText || 'Buy a {amount} gift card').replace('{amount}', a.label);
    buy.href = a.url || cfg.buyUrl;
  };
  amounts.forEach((a) => {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'amt'; b.setAttribute('role', 'radio');
    b.dataset.v = a.value; b.textContent = a.label; b.addEventListener('click', () => pick(a)); wrap.appendChild(b);
  });
  const def = amounts.find((a) => a.value === cfg.defaultAmount) || amounts[Math.min(2, amounts.length - 1)];
  if (def) pick(def);
  const gc = root.querySelector('.gc'), stage = root.querySelector('.gc-stage');
  if (!REDUCED && gc && stage) {
    stage.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch') return;
      const r = gc.getBoundingClientRect(), x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      gc.classList.add('live');
      gc.style.setProperty('--ry', ((x - .5) * 24).toFixed(2) + 'deg'); gc.style.setProperty('--rx', ((.5 - y) * 16).toFixed(2) + 'deg');
      gc.style.setProperty('--gx', (x * 100).toFixed(1) + '%'); gc.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
    });
    stage.addEventListener('pointerleave', () => { gc.classList.remove('live'); ['--rx', '--ry', '--gx', '--gy'].forEach((p) => gc.style.removeProperty(p)); });
  }
}

/* ---- Visit ---- */
function initVisit(root) {
  const cfg = data(root), H = makeHours(cfg);
  window.CMS_STATUS = H.status; // the trip map's packing list uses this ("We're open until 6 pm today")
  const paint = () => {
    const s = H.status(), vs = root.querySelector('.status');
    if (vs) { vs.classList.toggle('is-open', s.open); vs.lastElementChild.textContent = s.text; }
    const ul = root.querySelector('.hours');
    if (ul) ul.innerHTML = H.rows().map((r) => '<li' + (r[2] ? ' class="now"' : '') + '><span>' + r[0] + '</span><span>' + r[1] + '</span></li>').join('');
  };
  paint(); setInterval(paint, 60000);
  const t = solar(dayOfYear(ytNow())), sl = root.querySelector('.sunline');
  if (sl) sl.innerHTML = '<div><b>' + clock(t.rise) + '</b><span>Sunrise</span></div><div><b>' + clock(t.set) + '</b><span>Sunset</span></div><div><b>' + Math.floor(t.day / 60) + 'h ' + Math.round(t.day % 60) + 'm</b><span>Daylight</span></div>';
}

const INIT = { pro: initPro, gift: initGift, visit: initVisit };
function boot(scope) {
  (scope || document).querySelectorAll('.cms-sec').forEach((root) => {
    wireCopy(root);
    const kind = root.dataset.cms;
    if (INIT[kind] && !root.dataset.ready) { root.dataset.ready = '1'; INIT[kind](root); }
  });
}
boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));

/* ---- Menu jumps to homepage sections (/#visit, /#picks, /#store, /#pro-shop ...) ----
   Horizon resets the scroll when the phone menu closes and on page load, so the browser's own jump to #id
   is lost. Scroll to the section ourselves: after the menu closes, and once the page has loaded. */
const jumpTo = (el, smooth) => el.scrollIntoView({ block: 'start', behavior: smooth && !REDUCED ? 'smooth' : 'auto' });
const hashTarget = (hash) => { try { return hash && hash.length > 1 ? document.getElementById(decodeURIComponent(hash.slice(1))) : null; } catch (e) { return null; } };
if (!window.CMS_JUMPS) {
  window.CMS_JUMPS = true;
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href*="#"]');
    if (!a || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname) return;
    const el = hashTarget(url.hash);
    if (!el) return;
    e.preventDefault();
    const drawer = a.closest('details[open]');
    if (drawer) setTimeout(() => { if (drawer.open) drawer.querySelector('summary')?.click(); }, 60);
    history.pushState(null, '', url.hash);
    setTimeout(() => jumpTo(el, true), drawer ? 450 : 0);
  }, true);
  const el = hashTarget(location.hash);
  if (el) {
    let touched = false;
    ['wheel', 'touchstart', 'keydown'].forEach((t) => addEventListener(t, () => { touched = true; }, { once: true, passive: true }));
    // Horizon holds the scroll while its page transition plays, so keep trying until the section stays in place
    // (checked every 250 ms for up to 8 s; stops as soon as the visitor scrolls themselves)
    const scroller = () => { for (let p = el.parentElement; p; p = p.parentElement) { if (/(auto|scroll)/.test(getComputedStyle(p).overflowY) && p.scrollHeight > p.clientHeight) return p; } return document.scrollingElement; };
    const placed = () => { const top = el.getBoundingClientRect().top; return top >= -4 && top <= 160; };
    let tries = 0, settled = 0;
    const tick = () => {
      if (touched || ++tries > 32) return;
      if (placed()) { if (++settled >= 4) return; } else {
        settled = 0;
        jumpTo(el, false);
        if (!placed()) { const sc = scroller(); sc.scrollTop += el.getBoundingClientRect().top - 100; }
      }
      setTimeout(tick, 250);
    };
    setTimeout(tick, 50);
  }
}
